'use client';

import { X } from 'lucide-react';
import type React from 'react';

import { CompetitionType, type InviteServices } from '../InviteFriendsPage';

import { InviteByLink } from './InviteByLInk';
import SearchByName from './SearchByName';

import { Button, InputField, Tabs } from '@/components';
import useAuth from '@/hooks/useAuth';

interface InviteFriendsProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  formData: {
    name: string;
    email: string;
  };
  onInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onInputBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
  onSubmit: () => void;
  showSuccess: boolean;
  setShowSuccess: (show: boolean) => void;
  errors: {
    name?: string;
    email?: string;
  };
  idTorneo: number | null;
  onNavigateToTourney: () => void;
  onInviteById: (userId: number) => Promise<boolean | undefined>;
  type: 'tournament' | 'cup' | 'minileagues' | string;
  services: InviteServices;
  isDesktop?: boolean;
}

export default function InviteFriends({
  activeTab,
  setActiveTab,
  formData,
  onInputChange,
  onInputBlur,
  onSubmit,
  showSuccess,
  setShowSuccess,
  errors,
  idTorneo,
  onNavigateToTourney,
  onInviteById,
  type,
  services,
  isDesktop = false,
}: Readonly<InviteFriendsProps>) {
  const { userInitialData } = useAuth();

  const handleInvite = (userId: number): Promise<boolean | undefined> => {
    return onInviteById(userId);
  };

  const tabOptions = [
    { name: 'Por Nombre' },
    { name: 'Por E-mail' },
    { name: 'Por Link' },
  ];

  const handleTabChange = (index: number) => {
    const tabMapping = ['name', 'email', 'link'];
    setActiveTab(tabMapping[index]);
  };

  // Función para validar email
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Verificar si el email es válido (asegurando que sea un booleano)
  const emailIsValid = Boolean(
    formData.email && isValidEmail(formData.email) && !errors.email
  );

  return (
    <div className="rounded-lg">
      <h1 className="text-[#333333] text-base font-semibold mb-4">
        Invitar amigos
      </h1>

      <div className="bg-white rounded-lg overflow-hidden shadow-md">
        <Tabs
          activeTab={['name', 'email', 'link'].indexOf(activeTab)}
          className="!max-w-none"
          tabs={tabOptions}
          variant="underline"
          onClick={handleTabChange}
        />

        <div className="px-8 py-10 relative">
          {activeTab === 'name' && (
            <SearchByName
              idTorneo={idTorneo ?? 0}
              isDesktop={isDesktop}
              searchService={services.searchByName}
              suggestUsersService={services.suggestUsers}
              type={type}
              onInvite={handleInvite}
            />
          )}

          {activeTab === 'email' && (
            <div className="space-y-4">
              {type === 'cup' && (
                <p
                  className={`${
                    !isDesktop
                      ? 'text-[#333333] text-base leading-tight mb-6'
                      : 'text-md font-medium text-[#333333] mb-6'
                  }`}
                >
                  Recordá que el equipo suma con los puntos de tu 11 y el de tus
                  3 amigos. Si no está completo, no sumará puntos.
                </p>
              )}
              <div>
                <label className="block text-sm mb-2 font-bold" htmlFor="name">
                  Nombre del DT
                </label>
                <InputField
                  behavior="default"
                  error={errors.name}
                  id="name"
                  maxLength={50}
                  name="name"
                  placeholder="Ingresar nombre"
                  value={formData.name}
                  onBlur={onInputBlur}
                  onChange={onInputChange}
                />
              </div>
              <div>
                <label className="block text-sm mb-2 font-bold" htmlFor="email">
                  E-mail del DT
                </label>
                <InputField
                  behavior="default"
                  error={errors.email}
                  id="email"
                  maxLength={50}
                  name="email"
                  placeholder="Ingresar e-mail"
                  success={emailIsValid}
                  type="email"
                  value={formData.email}
                  onBlur={onInputBlur}
                  onChange={onInputChange}
                />
              </div>
              <Button
                className="!bg-[#e3eaff] !text-[#3366ff]"
                disabled={
                  !formData.name ||
                  !formData.email ||
                  !!errors.name ||
                  !!errors.email ||
                  formData.email === userInitialData?.mail
                }
                type="button"
                variant="secondary"
                onClick={onSubmit}
              >
                Enviar invitación
              </Button>
            </div>
          )}

          {activeTab === 'link' && (
            <InviteByLink
              idTorneo={String(idTorneo)}
              isDesktop={isDesktop}
              type={type}
            />
          )}

          {showSuccess && (
            <div className="absolute inset-0 bg-[#333333]/90 flex items-center justify-between p-4 text-white">
              <span>Tu invitación fue enviada.</span>
              <button onClick={() => setShowSuccess(false)}>
                <X />
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="mt-6">
        <Button
          className="w-full lg:w-auto !bg-[#3366ff] text-white px-6 py-2"
          type="button"
          variant="primary"
          onClick={onNavigateToTourney}
        >
          {type === CompetitionType.CUP
            ? 'Ir al Equipo'
            : type === CompetitionType.TOURNAMENT
            ? 'Ir al Torneo'
            : 'Ir a la Miniliga'}
        </Button>
      </div>
    </div>
  );
}
